import {faFileSpreadsheet, faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {BoxMod, Button, Center, Group, Image, Tooltip} from '@mantine/core';
import {DependencyList, FC, useCallback, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {useAsync} from 'react-use';
import {isFalsy} from 'utility-types';
import {JobTypes, Roles} from '../api/generated/enums.ts';
import {JobSummaryDto} from '../api/generated/index.defs.ts';
import {JobsService} from '../api/generated/JobsService';
import {UsersService} from '../api/generated/UsersService.ts';
import {useRequireAuthorization} from '../auth/require-authorization.tsx';
import {BasicPage} from '../components/basic-page.tsx';
import {EditButton} from '../hooks/paged-data-table/row-buttons.tsx';
import {
  PagedDataTableConfig,
  usePagedDataTable,
} from '../hooks/paged-data-table/use-paged-data-table.tsx';
import {useProduce} from '../hooks/use-produce.ts';
import {ROUTES} from '../routes';
import {AdvancedPagedRequest} from '../types';
import {getColorForStatus} from './job-highlight.tsx';
import {
  ComponentState,
  FilterSchema,
  TableFilters,
} from './job-listing-filter.tsx';
import {trimSerialNumber} from '../utils/string-helpers.ts';
import {ColumnConfig} from '../hooks/paged-data-table/types.ts';
import _ from 'lodash';
import {renderedDateFormat} from '../utils/luxon.ts';
import mmIcon from '../assets/mm_logo.png';
import classes from './job-listing.module.css';

export type JobListingProps = {
  initialValues?: FilterSchema;
  withoutFilters?: boolean;
  withoutActions?: boolean;
  withoutColumns?: (keyof JobSummaryDto)[];
};

type useTableConfigProps = {
  withoutColumns?: (keyof JobSummaryDto)[];
  userPartnerSiteIds?: number[] | null | undefined;
};

export const JobListing: FC<JobListingProps> = ({
  initialValues,
  withoutFilters,
  withoutActions,
  withoutColumns,
}) => {
  const [clear, setClear] = useState<boolean>(false);
  const [state, setState] = useProduce<ComponentState>({
    filter: {...initialValues},
  });

  const onExportCsv = useCallback(async () => {
    const {data, hasErrors} = await JobsService.getAllJobsAsCsv(state.filter);
    if (!hasErrors) window.location.href = String(data);
  }, [state.filter]);

  const runtimeConfig = {
    renderActions: !withoutActions && (
      <Group>
        <Button variant='outline'>
          <Group gap='xs' onClick={onExportCsv}>
            <FontAwesomeIcon icon={faFileSpreadsheet} /> Export to CSV
          </Group>
        </Button>
        <Button
          component={Link}
          to={ROUTES.ADMIN.JOBS.CREATE.buildPath({jobType: JobTypes['M&M']})}
        >
          <Group gap='xs'>
            <FontAwesomeIcon icon={faPlus} /> New M&M Job
          </Group>
        </Button>
        <Button
          component={Link}
          to={ROUTES.ADMIN.JOBS.CREATE.buildPath({
            jobType: JobTypes['Non-M&M'],
          })}
        >
          <Group gap='xs'>
            <FontAwesomeIcon icon={faPlus} /> New Non-M&M Job
          </Group>
        </Button>
      </Group>
    ),
    renderFilterAboveTable: true,
  };

  const onStateChange = useCallback(
    (tableState: AdvancedPagedRequest<JobSummaryDto>) => {
      setState((draft) => {
        draft.tableState = tableState;
      });
    },
    [setState],
  );

  const clearFilterState = useCallback(() => {
    setState((draft) => {
      draft.filter = {};
    });
    setClear(true);
  }, [setState]);

  const fetchUserPartnerSiteIds = useAsync(async () => {
    const {data} = await UsersService.getPartnerSiteIds();
    return data;
  });

  const TableConfig = useTableConfig({
    withoutColumns,
    userPartnerSiteIds: fetchUserPartnerSiteIds.value,
  });

  const getActiveFiltersCount = (obj: FilterSchema) => {
    if (!obj) return 0;
    return Object.values(obj).filter((x) => !isFalsy(x)).length;
  };

  const getTableFilters = useCallback(
    (dismiss) => (
      <TableFilters
        defaultValues={state.filter as FilterSchema}
        clear={clear}
        setClear={setClear}
        onSubmit={(filters) => {
          setState((draft) => {
            Object.keys(filters).forEach(
              (key) => (draft.filter[key] = filters[key]),
            );
            if (filters.serialNumber)
              draft.filter.serialNumber = '0' + filters.serialNumber;
          });

          dismiss();
        }}
        userPartnerSiteIds={fetchUserPartnerSiteIds.value}
      />
    ),
    [clear, fetchUserPartnerSiteIds.value, setState, state.filter],
  );

  const pagedDataTable = usePagedDataTable(JobsService.getAll, TableConfig, {
    ...runtimeConfig,
    additionalParams: state.filter,
    onStateChange,
    clearFilterState,
    renderFilter: !withoutFilters ? getTableFilters : undefined,
    filterBadge: !withoutFilters && getActiveFiltersCount(state.filter),
  });

  if (fetchUserPartnerSiteIds.loading) return null;

  return (
    <BasicPage
      title={initialValues?.createdDate ? 'Service History' : 'Jobs'}
      maxWidth='100%'
      pt={32}
    >
      {pagedDataTable}
    </BasicPage>
  );
};

type RenderedEditButtonProps = {
  id: number;
  partnerSiteId: number;
  isAuthorized: boolean;
  userPartnerSiteIds: number[] | null | undefined;
};

const RenderedEditButton: FC<RenderedEditButtonProps> = ({
  id,
  isAuthorized,
  partnerSiteId,
  userPartnerSiteIds,
}) => {
  const isAuthorizedToEdit = useMemo(
    () => isAuthorized || userPartnerSiteIds?.some((x) => x === partnerSiteId),
    [isAuthorized, partnerSiteId, userPartnerSiteIds],
  );

  const item = useMemo(() => ({id: id}), [id]);

  return (
    <>
      {isAuthorizedToEdit ? (
        <EditButton
          item={item}
          descriptor='job'
          route={ROUTES.ADMIN.JOBS.DETAIL.path}
        />
      ) : null}
    </>
  );
};

const getHighlightText = (value: boolean | undefined) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Unk.';
};

const getHighlightColor = (value: boolean | undefined) => {
  if (value === true) return getColorForStatus('evaluationIsRepairable', 'yes');
  if (value === false) return getColorForStatus('evaluationIsRepairable', 'no');
  return getColorForStatus('evaluationIsRepairable', 'unk');
};

const renderedNullableBoolean = (value: boolean | undefined) => {
  return getHighlightText(value);
};

const useTableConfig = ({withoutColumns, userPartnerSiteIds}: useTableConfigProps) => {
  const isAuthorized = useRequireAuthorization({role: [Roles['Global Admin']]});

  const columns = useMemo(
    () =>
      [
        {
          title: '',
          accessor: '$_EDIT_$',
          render: (item) => (
            <RenderedEditButton
              id={Number(item.id)}
              partnerSiteId={Number(item.partnerSiteId)}
              userPartnerSiteIds={userPartnerSiteIds}
              isAuthorized={isAuthorized}
            />
          ),
        },
        {
          accessor: 'partnerSiteName',
          title: 'Partner Site Name',
          sortable: true,
          render: (item) => item.partnerSiteName,
        },
        {
          accessor: 'partnerSiteArfNumber',
          title: 'Arf No.',
          hidden: !isAuthorized && userPartnerSiteIds?.length === 1,
          render: (item) => item.partnerSiteArfNumber ?? '',
        },
        {
          accessor: 'id',
          sortable: true,
          render: (item) => item.id ?? '',
        },
        {
          accessor: 'status',
          title: 'Job Status',
          cellsStyle: (item) => ({
            backgroundColor: getColorForStatus('status', item.status),
          }),
          render: (item) => item.status ?? '',
        },
        {
          accessor: 'evaluationIsRepairable',
          title: 'Repairable',
          cellsStyle: (item) => ({
            backgroundColor: getHighlightColor(item.evaluationIsRepairable),
          }),
          render: (item) =>
            renderedNullableBoolean(item.evaluationIsRepairable),
        },
        {
          accessor: 'serialNumber',
          title: 'Serial No.',
          sortable: true,
          render: (item) => trimSerialNumber(item.serialNumber),
        },
        {
          accessor: 'partNumber',
          title: 'Part No.',
          sortable: true,
          render: (item) => item.partNumber,
        },
        {
          accessor: 'levelOfServiceDescription',
          title: 'Level of Service',
          sortable: true,
          render: (item) => item.levelOfServiceDescription,
        },
        {
          accessor: 'clientName',
          title: 'Client',
          sortable: true,
          render: (item) => item.clientName,
        },
        {
          accessor: 'assetNumber',
          title: 'Asset No.',
          sortable: true,
          render: (item) => item.assetNumber,
        },
        {
          accessor: 'reference1',
          title: 'Ref 1',
          sortable: true,
          render: (item) => item.reference1,
        },
        {
          accessor: 'createdDate',
          title: 'Job Created',
          sortable: true,
          render: (item) => renderedDateFormat(item.createdDate),
        },
        {
          accessor: 'evaluationCompleteDate',
          title: 'Evaluation Complete',
          sortable: true,
          render: (item) => renderedDateFormat(item.evaluationCompletedDate),
        },
        {
          accessor: 'evaluationQuoteStatus',
          title: 'Quote Status',
          cellsStyle: (item) => {
            if (!!item.evaluationQuoteStatus) {
              return {
                backgroundColor: getColorForStatus(
                  'evaluationQuoteStatus',
                  item.evaluationQuoteStatus,
                ),
              };
            }
          },
          render: (item) => item.evaluationQuoteStatus,
        },
        {
          accessor: 'reviewStatus',
          title: 'Review Status',
          cellsStyle: (item) => ({
            backgroundColor: getColorForStatus(
              'reviewStatus',
              item.reviewStatus,
            ),
          }),
          render: (item) => item.reviewStatus ?? '',
        },
        {
          accessor: 'closedArchivedDate',
          title: 'Archived',
          sortable: true,
          render: (item) => renderedDateFormat(item.dateClosed),
        },
        {
          accessor: 'certScrapReportIssuedDate',
          title: 'Cert/Scrap Report',
          sortable: true,
          render: (item) => renderedDateFormat(item.certScrapReportIssuedDate),
        },
        {
          accessor: 'jobType',
          title: '',
          render: (item) => (
            <Tooltip
              label={
                item.jobType === JobTypes['M&M'] ? 'M&M Job' : 'Non-M&M Job'
              }
            >
              <Center>
                <Image
                  src={mmIcon}
                  w={20}
                  mod={getModAttributes(item)}
                  className={classes.mmIcon}
                />
              </Center>
            </Tooltip>
          ),
        },
      ] satisfies ColumnConfig<JobSummaryDto>[],
    [userPartnerSiteIds, isAuthorized],
  );

  const filteredColumns = useMemo(() => {
    return _.filter(
      columns,
      (x) => !_.some(withoutColumns, (y) => y === x.accessor),
    );
  }, [columns, withoutColumns]);

  return useMemo(() => {
    return PagedDataTableConfig(JobsService.getAll, {
      columns: filteredColumns,
      searchFieldNames: ['id', 'serialNumber'],
    });
  }, [filteredColumns]);
};

const getModAttributes = (item: JobSummaryDto): BoxMod => ({
  grayscale: Boolean(item.jobType === JobTypes['Non-M&M']),
});